import { useCallback, useMemo, useState, type ReactNode } from "react";
import styled from "styled-components";
import { useResizeObserver } from "hooks/useResizeObserver";

const MASONRY_BREAKPOINT_PX = 500;
const WIDE_GALLERY_BREAKPOINTS = { 0: 1, [MASONRY_BREAKPOINT_PX]: 2 };
const NARROW_GALLERY_BREAKPOINTS = { 0: 1 };

const VERTICAL_ASPECT_THRESHOLD = 0.85;
const CHUNK_SIZE = 2;
const VERTICAL_CHUNK_PREFIX = "vertical-";
const WIDE_CHUNK_PREFIX = "wide-";
const ITEM_GAP_PX = 8;

type Breakpoints = { [key: number]: number };

type RenderGroupArgs<T> = {
  groupKey: string;
  items: T[];
  renderItem: (item: T, indexWithinGroup: number) => ReactNode;
};

type InterleaveResult<T extends object> = {
  galleryAssets: T[];
  chunkKeyByAsset: WeakMap<T, string>;
};

const interleavePairs = <T extends object>(
  items: T[],
  getAspectRatio: (item: T) => number | null
): InterleaveResult<T> => {
  const verticals: T[] = [];
  const wides: T[] = [];
  for (const item of items) {
    const aspect = getAspectRatio(item);
    if (aspect !== null && aspect < VERTICAL_ASPECT_THRESHOLD) {
      verticals.push(item);
    } else {
      wides.push(item);
    }
  }

  const ordered: T[] = [];
  const chunkKeyByAsset = new WeakMap<T, string>();
  let wideIdx = 0;
  let verticalIdx = 0;
  let wideChunkCount = 0;
  let verticalChunkCount = 0;

  while (wideIdx < wides.length || verticalIdx < verticals.length) {
    if (wideIdx < wides.length) {
      const key = `${WIDE_CHUNK_PREFIX}${wideChunkCount++}`;
      for (let i = 0; i < CHUNK_SIZE && wideIdx < wides.length; i++) {
        const item = wides[wideIdx++];
        if (!item) continue;
        chunkKeyByAsset.set(item, key);
        ordered.push(item);
      }
    }
    if (verticalIdx < verticals.length) {
      const key = `${VERTICAL_CHUNK_PREFIX}${verticalChunkCount++}`;
      for (let i = 0; i < CHUNK_SIZE && verticalIdx < verticals.length; i++) {
        const item = verticals[verticalIdx++];
        if (!item) continue;
        chunkKeyByAsset.set(item, key);
        ordered.push(item);
      }
    }
  }

  return { galleryAssets: ordered, chunkKeyByAsset };
};

const makeRenderGroup = <T extends object>(
  getAspectRatio: (item: T) => number | null
) => {
  const renderGroup = ({
    groupKey,
    items,
    renderItem,
  }: RenderGroupArgs<T>): ReactNode => {
    const isVertical = groupKey.startsWith(VERTICAL_CHUNK_PREFIX);

    if (isVertical) {
      // Below 500px container: stack vertically (1 column).
      // At >=500px: justified row where each child's flex equals its aspect ratio,
      // so the available width is split inversely-proportional to height — making
      // both images render at the same height.
      return (
        <VerticalChunkRow>
          {items.map((item, indexWithinGroup) => {
            const aspect = getAspectRatio(item) ?? 1;
            return (
              <VerticalChunkItem key={indexWithinGroup} $aspect={aspect}>
                {renderItem(item, indexWithinGroup)}
              </VerticalChunkItem>
            );
          })}
        </VerticalChunkRow>
      );
    }

    // Wide / square chunk: stacked single column.
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: ITEM_GAP_PX,
          marginBottom: ITEM_GAP_PX,
        }}
      >
        {items.map((item, indexWithinGroup) => (
          <div key={indexWithinGroup}>{renderItem(item, indexWithinGroup)}</div>
        ))}
      </div>
    );
  };
  return renderGroup;
};

const VerticalChunkRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${ITEM_GAP_PX}px;
  margin-bottom: ${ITEM_GAP_PX}px;
`;

const VerticalChunkItem = styled.div<{ $aspect: number }>`
  min-width: 0;
  flex: ${({ $aspect }) => $aspect};
`;

export type MasonryGalleryLayout<T> = {
  galleryRef: React.RefObject<HTMLDivElement>;
  breakpoints: Breakpoints;
  galleryAssets: T[];
  groupBy: ((item: T) => string) | undefined;
  renderGroup: ((args: RenderGroupArgs<T>) => ReactNode) | undefined;
};

export const useMasonryGalleryLayout = <T extends object>({
  items,
  getAspectRatio,
}: {
  items: T[];
  getAspectRatio: (item: T) => number | null;
}): MasonryGalleryLayout<T> => {
  const [galleryWidth, setGalleryWidth] = useState(0);

  const handleGalleryResize = useCallback(
    (size: { width: number; height: number }) => setGalleryWidth(size.width),
    []
  );
  const galleryRef = useResizeObserver(handleGalleryResize);

  const isWideGallery = galleryWidth > MASONRY_BREAKPOINT_PX;

  const { galleryAssets, chunkKeyByAsset } = useMemo(() => {
    if (isWideGallery) {
      return {
        galleryAssets: items,
        chunkKeyByAsset: new WeakMap<T, string>(),
      };
    }
    return interleavePairs(items, getAspectRatio);
  }, [items, isWideGallery, getAspectRatio]);

  const groupBy = useCallback(
    (item: T) => chunkKeyByAsset.get(item) ?? WIDE_CHUNK_PREFIX,
    [chunkKeyByAsset]
  );

  const renderGroup = useMemo(
    () => makeRenderGroup<T>(getAspectRatio),
    [getAspectRatio]
  );

  return {
    galleryRef,
    breakpoints: isWideGallery
      ? WIDE_GALLERY_BREAKPOINTS
      : NARROW_GALLERY_BREAKPOINTS,
    galleryAssets,
    groupBy: isWideGallery ? undefined : groupBy,
    renderGroup: isWideGallery ? undefined : renderGroup,
  };
};
